/** @file slot.fitPivot: entry pivots proposed from the art (bone-entry), written with apply (CR-012). */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { checkBoolean } from '@zilo/pose3d-format/validate/fields/checkBoolean';
import { checkNumber } from '@zilo/pose3d-format/validate/fields/checkNumber';
import { checkRecord } from '@zilo/pose3d-format/validate/fields/checkRecord';
import { checkShape } from '@zilo/pose3d-format/validate/fields/checkShape';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import type { Command } from '../types/command';
import type { PivotFit, PivotPartKind } from '../types/puppetInputs';
import { compact } from '../validate/compact';
import { pointerToken } from '../validate/pointerToken';
import { readInput } from '../validate/readInput';
import type { FitTarget } from './pivot/fitTargetsOf';
import { fitTargetsOf } from './pivot/fitTargetsOf';

const KINDS = ['limb', 'hanging', 'pinned', 'torso', 'head'] as const satisfies readonly PivotPartKind[];
/** Below this confidence a tile's reply carries a hint to look at the art. */
const LOW_CONFIDENCE = 0.5;

/** A measured fit as the server sends it (shape only; the numbers are the server's). */
const fitCheck = checkShape({ required: {
  pivot: (v, path) => { if (v !== null && !(Array.isArray(v) && v.length === 2 && v.every((n) => typeof n === 'number' && Number.isFinite(n)))) failField(path, '[x, y] or null', v); },
  confidence: checkNumber({ min: 0, max: 1 }), kind: (v, path) => { if (!KINDS.includes(v as PivotPartKind)) failField(path, `one of ${KINDS.join(', ')}`, v); },
  snapped: checkBoolean, reversed: checkBoolean,
} });
const measuredCheck = checkRecord(fitCheck, /^.+$/);

const half = (v: number): number => Math.round(v * 2) / 2;
const median = (xs: number[]): number => {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 === 1 ? (s[m] ?? 0) : ((s[m - 1] ?? 0) + (s[m] ?? 0)) / 2;
};

/** What a tile's fit says to the agent beyond its numbers. */
function hintOf(fit: PivotFit): string | undefined {
  if (fit.pivot === null) return 'no opaque pixel in this tile: nothing to measure';
  if (fit.reversed) return 'the opaque pixels lie at the far end from the joint: the art hangs the wrong way (flip it along the bone), or the part is drawn from its other end (set kind or the drawn screen angle); do not move the pivot there';
  if (fit.confidence < LOW_CONFIDENCE) return 'few columns agree on where the bone enters the art: check this tile with render { overlay: joints }';
  return undefined;
}

/** One tile's reply row. */
function tileRow(t: FitTarget, fit: PivotFit): Record<string, unknown> {
  const [w, h] = t.slot.size;
  const hint = hintOf(fit);
  return compact({
    slot: t.slot.key, pivot: fit.pivot, pivotFrac: fit.pivot === null ? null : [fit.pivot[0] / w, fit.pivot[1] / h],
    confidence: fit.confidence, kind: fit.kind, snapped: fit.snapped, reversed: fit.reversed, hint,
  });
}

/** Per `<drawable>/<entry>`, the tiles measured on it. */
function byEntry(targets: FitTarget[]): Map<string, FitTarget[]> {
  const out = new Map<string, FitTarget[]>();
  for (const t of targets) {
    const key = `${t.drawable.id}/${t.slot.entry}`;
    out.set(key, [...(out.get(key) ?? []), t]);
  }
  return out;
}

/**
 * `slot.fitPivot { slot | drawable, mode?: 'bone-entry', kind?, apply?, measured }` → `{ mode, applied, tiles, entries }`.
 * The server measures every target tile (commands `measurePivot`, the rule in CR-012: walk from the joint-side border
 * along `drawnScreenAngle` to the first opaque row or column; across, the centre column `floor((w - 1) / 2)`, which
 * `halfPixel` shifts to the centre; defaults by kind: limb fy 0.75..1.0 with the joint at the bottom, hanging (tail,
 * cape) 0.07 from the top, pinned 0.25..0.375, torso 1/3, head the centre; a walk within 1 px snaps onto them) and
 * fills `measured`. Each entry's proposal is the per-axis median of its tiles, on half pixels; `apply` writes it to
 * `pivotFrac` (inverse: one `facet.setEntry` per changed entry). `reversed` tiles carry a hint.
 */
export const slotFitPivotCommand: CommandDef<'slot.fitPivot'> = {
  name: 'slot.fitPivot',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['slot', 'drawable', 'mode', 'kind', 'apply', 'measured']);
    return compact({
      slot: r.opt('slot', r.str), drawable: r.opt('drawable', r.str), mode: r.opt('mode', (k) => r.oneOf(k, ['bone-entry'] as const)),
      kind: r.opt('kind', (k) => r.oneOf(k, KINDS)), apply: r.opt('apply', r.bool),
      measured: r.opt('measured', (k) => ({ ...(r.check(k, measuredCheck) as Record<string, PivotFit>) })),
    });
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input) => {
    const targets = fitTargetsOf(draft.puppet, input);
    const measured = input.measured ?? {};
    const fits = targets.map((t) => measured[t.slot.key] ?? throwError(ERROR_CODES.VALIDATION, { slot: t.slot.key }, {
      path: `/input/measured/${pointerToken(t.slot.key)}`, hint: 'send slot.fitPivot alone through batch (REST or MCP): the server measures the art and fills measured',
    }));
    const fitOf = new Map(targets.map((t, i) => [t, fits[i] as PivotFit]));
    const inverse: Command[] = [];
    const entries = [...byEntry(targets).values()].map((tiles) => {
      const first = tiles[0] as FitTarget;
      const [w, h] = first.entry.size;
      const pivots = tiles.flatMap((t) => { const p = fitOf.get(t)?.pivot; return p === null || p === undefined ? [] : [p]; });
      const previous: [number, number] = [...first.entry.pivotFrac];
      const confidence = Math.round((tiles.reduce((a, t) => a + (fitOf.get(t)?.confidence ?? 0), 0) / tiles.length) * 100) / 100;
      if (pivots.length === 0) return { drawable: first.drawable.id, entry: first.slot.entry, pivotFrac: null, previous, confidence, tiles: tiles.length, applied: false };
      const pivotFrac: [number, number] = [half(median(pivots.map((p) => p[0]))) / w, half(median(pivots.map((p) => p[1]))) / h];
      const changed = pivotFrac[0] !== previous[0] || pivotFrac[1] !== previous[1];
      const applied = input.apply === true && changed;
      if (applied) {
        first.entry.pivotFrac = pivotFrac;
        inverse.push({ name: 'facet.setEntry', input: { drawable: first.drawable.id, entry: first.slot.entry, fields: { pivotFrac: previous } } });
      }
      return { drawable: first.drawable.id, entry: first.slot.entry, pivotFrac, previous, confidence, tiles: tiles.length, applied };
    });
    const tiles = targets.map((t) => tileRow(t, fitOf.get(t) as PivotFit));
    return { result: { mode: input.mode ?? 'bone-entry', applied: inverse.length > 0, tiles, entries }, warnings: [], inverse };
  },
};
